/**
 * O campo de data — 11.1 · `telas/11.1-portal-parceiro.html:517`
 * ("dv-date compacto (De/Até do Personalizado) — padrão canônico").
 *
 * ## O acordo é o MESMO do `SeletorDoSistema`, e pela mesma razão
 *
 * O `<input type="date">` continua sendo o controle. Ele guarda o valor no formato que a
 * API fala (`YYYY-MM-DD`), responde ao teclado do sistema, é o que o leitor de tela
 * anuncia, é a roda nativa no celular — e é o que os `fill('2026-08-29')` de sete specs
 * encontram. O que muda é que, com ponteiro fino em tela larga, o indicador nativo do
 * Chrome some e por cima vai uma capa desenhada, que abre o calendário do protótipo em
 * vez do calendário do sistema operacional. Foi o mesmo desenho que a decisão C de 31/08
 * aplicou ao `<select>`, e a razão inteira está no cabeçalho daquele componente.
 *
 * ## Este era o último item do protótipo por transportar
 *
 * `app/CLAUDE.md` registra: *"Falta: só o calendário `dv-date`"*. O diagnóstico de 01/09
 * afirmava que **não havia** desenho de calendário no protótipo e que este seria desenho
 * novo — **há**: a 11.1 traz a grade inteira e se declara o padrão canônico. Daí vêm as
 * medidas deste arquivo, transportadas e não reinventadas: popover `w-64`, dia `h-7`,
 * cabeçalho `text-sm font-medium text-navy`, semana `text-[11px] text-muted`, e o dia de
 * hoje com `ring-1 ring-navy`.
 *
 * ## O que a capa NÃO faz
 *
 * Não guarda o valor. Ao escolher um dia ela escreve no `<input>` pelo setter nativo e
 * dispara um `change` que borbulha — o mesmo caminho do seletor. Assim o React vê
 * exatamente o evento que veria se a pessoa tivesse usado o calendário do sistema, e
 * `min`, `max` e `required` continuam sendo do input, não uma segunda regra aqui.
 */
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { deInstanteUTC } from '@dv/domain'

import { Icone } from '../shell/icone.tsx'
import { useDatas, usePreferencias } from '../shell/contexto-de-preferencias.tsx'
import { useConsultaDeTela } from './indice-lateral.tsx'
import { useTextos } from '../textos.ts'
import { DIAS_DO_CALENDARIO, MESES_DO_CALENDARIO, TEXTOS_DO_CALENDARIO } from './calendario-textos.ts'
import { foraDoConjunto, PainelFlutuante, usePosicaoDoPainel } from './painel-flutuante.tsx'

/** `w-64` do protótipo, em número: a geometria precisa da medida, não da classe. */
const LARGURA_DO_CALENDARIO = 256
/** Cabeçalho + semana + seis linhas de dias + rodapé, medido no protótipo. */
const ALTURA_DO_CALENDARIO = 330

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/

/** O mês que o calendário deve abrir mostrando: o do valor, ou o de hoje quando vazio. */
function mesDoValor(iso: string, hoje: string): { ano: number; mes: number } {
  const m = ISO.exec(iso)
  if (m === null) return { ano: Number(hoje.slice(0, 4)), mes: Number(hoje.slice(5, 7)) - 1 }
  return { ano: Number(m[1]), mes: Number(m[2]) - 1 }
}

/**
 * Data local → `YYYY-MM-DD`.
 *
 * Montada à mão de propósito: `toISOString()` converte para UTC, e às 21h de Brasília ela
 * devolve o DIA SEGUINTE. Data nominal não tem fuso (regra do `app/CLAUDE.md`).
 */
function comoIso(ano: number, mes: number, dia: number): string {
  return `${String(ano).padStart(4, '0')}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`
}

/**
 * `forwardRef` porque o `Campo` é `forwardRef` e passa o ref adiante: é assim que a tela
 * põe o foco no primeiro campo com erro. Perder o ref aqui quebraria esse foco justamente
 * nos campos de data, que é onde ele mais aparece (vencimento, competência, vigência).
 */
export const CampoDeData = forwardRef<HTMLInputElement, {
  /** A largura vai aqui, não no `className` — mesma razão do `SeletorDoSistema`. */
  classeDaCaixa?: string
} & React.InputHTMLAttributes<HTMLInputElement>>(function CampoDeData({
  className = 'fld',
  classeDaCaixa = '',
  ...resto
}, refDeFora): React.JSX.Element {
  const datas = useDatas()
  const { formatoDeData, idioma } = usePreferencias()
  const texto = useTextos(TEXTOS_DO_CALENDARIO)
  // A ação Hoje escreve uma data de negócio; o fuso preferido não muda esse dia.
  const isoDeHoje = deInstanteUTC(new Date().toISOString())
  const decora = useConsultaDeTela('(min-width: 768px) and (pointer: fine)')
  const [aberto, setAberto] = useState(false)
  const caixa = useRef<HTMLDivElement>(null)
  const nativo = useRef<HTMLInputElement>(null)

  const valor = String(resto.value ?? '')
  const inerte = resto.disabled === true
  const [mostrando, setMostrando] = useState(() => mesDoValor(valor, isoDeHoje))

  /* Abrir mostra o mês do VALOR, não o mês onde a navegação parou da última vez. */
  useEffect(() => {
    if (aberto) setMostrando(mesDoValor(valor, isoDeHoje))
  }, [aberto, valor, isoDeHoje])

  /*
    A medida sai da CAIXA do campo, e não do `<input>`: a capa desenhada cobre a caixa
    inteira, e é a borda dela que a pessoa vê.
  */
  const medirCampo = useCallback(() => caixa.current?.getBoundingClientRect() ?? null, [])
  const geometria = usePosicaoDoPainel(aberto, medirCampo, {
    alturaDesejada: ALTURA_DO_CALENDARIO,
    largura: LARGURA_DO_CALENDARIO,
    alinhar: 'fim',
  })

  useEffect(() => {
    if (!aberto) return undefined
    /*
      O painel está PORTADO: ele não é mais descendente da caixa, então `contains`
      sozinho passou a responder "fora" para um clique dentro do próprio calendário —
      e o dia fechava o painel sem nunca receber o clique. `foraDoConjunto` pergunta
      pelos dois nós.
    */
    const foraDaCaixa = (evento: MouseEvent): void => {
      if (foraDoConjunto(evento.target, caixa.current)) setAberto(false)
    }
    const noEscape = (evento: KeyboardEvent): void => {
      const alvo = caixa.current
      if (evento.key !== 'Escape' || alvo?.closest('[inert]') !== null) return
      // O calendário consome o primeiro Escape antes do diálogo que o contém.
      // Com ele fechado, o próximo Escape continua chegando ao formulário.
      evento.preventDefault()
      evento.stopPropagation()
      setAberto(false)
      nativo.current?.focus()
    }
    document.addEventListener('mousedown', foraDaCaixa)
    document.addEventListener('keydown', noEscape, true)
    return () => {
      document.removeEventListener('mousedown', foraDaCaixa)
      document.removeEventListener('keydown', noEscape, true)
    }
  }, [aberto])

  /*
    A escrita passa pelo ELEMENTO, não por um evento sintético montado à mão — o mesmo
    caminho do `SeletorDoSistema`. Montar um objeto que finge ser um evento funcionaria
    hoje e quebraria no dia em que alguém lesse mais um campo dele.
  */
  const escolher = (iso: string): void => {
    setAberto(false)
    const no = nativo.current
    if (no === null) return
    Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')?.set?.call(no, iso)
    no.dispatchEvent(new Event('change', { bubbles: true }))
  }

  const andarMes = (passo: number): void => {
    setMostrando((atual) => {
      const m = atual.mes + passo
      if (m < 0) return { ano: atual.ano - 1, mes: 11 }
      if (m > 11) return { ano: atual.ano + 1, mes: 0 }
      return { ano: atual.ano, mes: m }
    })
  }

  /* `min`/`max` são do input, e a grade os respeita: dia fora da faixa não vira botão vivo. */
  const min = typeof resto.min === 'string' ? resto.min : ''
  const max = typeof resto.max === 'string' ? resto.max : ''
  const foraDaFaixa = (iso: string): boolean =>
    (min !== '' && iso < min) || (max !== '' && iso > max)

  const primeiro = new Date(mostrando.ano, mostrando.mes, 1).getDay()
  const diasNoMes = new Date(mostrando.ano, mostrando.mes + 1, 0).getDate()
  const escrito = datas.nominal(valor) ?? ''

  return (
    <div
      ref={caixa}
      className={`${decora ? 'dv-date relative' : 'relative'}${classeDaCaixa === '' ? '' : ` ${classeDaCaixa}`}`}
    >
      <input
        {...resto}
        ref={(no) => {
          /* Um nó, dois donos: o ref interno (que escreve o valor) e o de quem chama. */
          nativo.current = no
          if (typeof refDeFora === 'function') refDeFora(no)
          else if (refDeFora !== null) refDeFora.current = no
        }}
        type="date"
        className={`${className}${decora ? ' dv-date-nativo' : ''}`}
      />
      {decora ? (
        <>
          <div
            aria-hidden="true"
            data-teste="capa-da-data"
            {...(typeof resto.title === 'string' ? { title: resto.title } : {})}
            className={`${className} dv-date-capa${inerte ? ' dv-date-capa-inerte' : ''}`}
            onClick={() => {
              if (!inerte) setAberto((estava) => !estava)
            }}
          >
            <span className="dv-date-value tnum truncate">{escrito === '' ? formatoDeData.toLowerCase() : escrito}</span>
            <Icone nome="calendar" className="h-4 w-4 shrink-0 text-faint" />
          </div>
          {aberto ? (
            <PainelFlutuante
              geometria={geometria}
              data-teste="calendario"
              /*
                `overflow-y-auto`: quando nem em cima nem embaixo cabe o calendário
                inteiro — celular deitado, por exemplo —, a grade rola DENTRO do painel
                em vez de ser cortada pela borda da janela. O `z-[80]` fica acima do
                diálogo (`z-[75]`), porque agora o painel é irmão dele, não filho do campo.
              */
              className="dv-date-cal z-[80] overflow-y-auto rounded-[10px] border border-hairline bg-white p-3 shadow-card"
            >
              <div className="mb-2 flex items-center justify-between">
                <button
                  type="button"
                  aria-label={texto('anterior')}
                  className="grid h-7 w-7 place-items-center rounded-md text-muted hover:bg-navy/10"
                  onClick={() => {
                    andarMes(-1)
                  }}
                >
                  <Icone nome="chevron-left" className="h-4 w-4" />
                </button>
                <span className="text-sm font-medium text-navy">
                  {MESES_DO_CALENDARIO[idioma][mostrando.mes]} {mostrando.ano}
                </span>
                <button
                  type="button"
                  aria-label={texto('proximo')}
                  className="grid h-7 w-7 place-items-center rounded-md text-muted hover:bg-navy/10"
                  onClick={() => {
                    andarMes(1)
                  }}
                >
                  <Icone nome="chevron-right" className="h-4 w-4" />
                </button>
              </div>
              {/* D S T Q Q S S — a semana começa no domingo, como o nativo em pt-BR. */}
              <div className="mb-1 grid grid-cols-7 gap-1 text-center text-[11px] text-muted">
                {DIAS_DO_CALENDARIO[idioma].map((letra, i) => (
                  <span key={`${letra}-${String(i)}`}>{letra}</span>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-[12px]">
                {Array.from({ length: primeiro }, (_, i) => <span key={`vazio-${String(i)}`} />)}
                {Array.from({ length: diasNoMes }, (_, i) => {
                  const dia = i + 1
                  const iso = comoIso(mostrando.ano, mostrando.mes, dia)
                  const bloqueado = foraDaFaixa(iso)
                  const marca =
                    iso === valor
                      ? 'bg-navy font-semibold text-white'
                      : iso === isoDeHoje
                        ? 'font-semibold text-navy ring-1 ring-navy'
                        : 'text-text2'
                  return (
                    <button
                      key={iso}
                      type="button"
                      disabled={bloqueado}
                      data-dia={iso}
                      className={`h-7 rounded-md ${bloqueado ? 'cursor-not-allowed text-faint' : `hover:bg-navy/10 ${marca}`}`}
                      onClick={() => {
                        escolher(iso)
                      }}
                    >
                      {dia}
                    </button>
                  )
                })}
              </div>
              <div className="mt-2 flex items-center justify-between border-t border-hairline pt-2 text-[12px]">
                <button
                  type="button"
                  className="text-muted hover:text-navy"
                  onClick={() => {
                    escolher('')
                  }}
                >
                  {texto('limpar')}
                </button>
                <button
                  type="button"
                  className="font-medium text-navy dv-link"
                  onClick={() => {
                    escolher(isoDeHoje)
                  }}
                >
                  {texto('hoje')}
                </button>
              </div>
            </PainelFlutuante>
          ) : null}
        </>
      ) : null}
    </div>
  )
})
